import React, { useEffect, useState } from 'react';
import "./EditUserModal.scss";

import closeIcon from '../../../../../../Assets/images/close-icon.svg';
import editIcon from '../../../../../../Assets/images/edit-icon.svg';
import uploadIcon from '../../../../../../Assets/images/upload-icon.svg';
import humanIcon from "../../../../../../Assets/images/human.png";

import InfoTab from '../EditModalTabs/InfoTab/InfoTab';
import PasswordTab from '../EditModalTabs/PasswordTab/PasswordTab';
import apiClient from '../../../../../../Config/apiClient';
import { showErrorToast, showSuccessToast } from '../../../../../../Context/Toaster/Toaster';
import Loader from "../../../../../../Context/Loader/Loader";
import { ALLOWED_IMAGE_TYPES } from '../../../Utils/Constants';
import { isValidEmail } from "../../../../../../Utils/Validations/validateEmail";
import { isValidCanadianPhone } from "../../../../../../Utils/Validations/validatePhone";

const siteUserPath = process.env.REACT_APP_CC_SITE_USERS_BASE_PATH;
const rolesPath = process.env.REACT_APP_CC_ROLES_BASE_PATH;

const EditUserModal = ({ user, refreshUserList, canEditUser }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [formData, setFormData] = useState({});
  const [roles, setRoles] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [errors, setErrors] = useState({});

  const [profileImageFile, setProfileImageFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [isImageRemoved, setIsImageRemoved] = useState(false);

  useEffect(() => {
    if (user?.id) {
      const shouldEdit = !!user.openInEditMode && canEditUser;
      setIsEditMode(shouldEdit);
      setActiveTab('info');
      fetchUserDetails(user.id);
      fetchRoles();
    }
  }, [user]);

  useEffect(() => {
    const modal = document.getElementById('editUserModal');

    const handleModalShow = () => {
      const shouldEdit = !!user?.openInEditMode;
      setIsEditMode(shouldEdit);
      setActiveTab('info');
    };

    modal?.addEventListener('show.bs.modal', handleModalShow);

    return () => {
      modal?.removeEventListener('show.bs.modal', handleModalShow);
    };
  }, [user]);

  const fetchUserDetails = async (userId) => {
    try {
      setIsLoading(true);

      const response = await apiClient.get(`${siteUserPath}/get?id=${userId}`);
      if (response.data.status === 'SUCCESS') {
        const user = response.data.data;
        setUserDetails(user);
        setFormData({
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email_id: user.email_id || '',
          contact_no: user.contact_no || '',
          role_id: user.role?.id || '',
          role_name: user.role?.role_name || 'Unknown',
        });

        try {
          const imageRes = await apiClient.get(`${siteUserPath}/profile-image?user_id=${userId}`);
          if (imageRes.data.status === 'SUCCESS') {
            setPreviewImageUrl(imageRes.data.image_url);
            user.profile_image_url = imageRes.data.image_url;
          } else {
            setPreviewImageUrl('');
          }
        } catch (imgErr) {
          setPreviewImageUrl('');
        }

        setIsImageRemoved(false);
        setProfileImageFile(null);
      } else {
        showErrorToast(response.data.message || 'Failed to fetch user');
      }
    } catch (error) {
      showErrorToast('Failed to fetch user');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiClient.get(`${rolesPath}/list`);
      if (response.data.status === 'SUCCESS') {
        setRoles(response.data.data);
      } else {
        showErrorToast('Failed to fetch permissions');
      }
    } catch (error) {
      showErrorToast('Error fetching permissions');
    }
  };

  const validateFields = () => {
    const newErrors = {};

    if (!formData.first_name) {
      newErrors.first_name = 'First name is required.';
    } else if (formData.first_name.length > 50) {
      newErrors.first_name = 'Max 50 characters allowed.';
    }

    if (!formData.last_name) {
      newErrors.last_name = 'Last name is required.';
    } else if (formData.last_name.length > 50) {
      newErrors.last_name = 'Max 50 characters allowed.';
    }

    if (!formData.email_id) {
      newErrors.email_id = 'Email is required.';
    } else if (!isValidEmail(formData.email_id)) {
      newErrors.email_id = 'Invalid email format.';
    }

    if (!formData.contact_no) {
      newErrors.contact_no = 'Contact number is required.';
    } else if (!isValidCanadianPhone(formData.contact_no)) {
      newErrors.contact_no = 'Invalid Canadian phone number.';
    }

    if (!formData.role_id) {
      newErrors.role_id = 'Permission Name is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setActiveTab('info');
    if (userDetails) {
      setFormData({
        first_name: userDetails.first_name || '',
        last_name: userDetails.last_name || '',
        email_id: userDetails.email_id || '',
        contact_no: userDetails.contact_no || '',
        role_id: userDetails.role?.id || '',
        role_name: userDetails.role?.role_name || '',
      });

      setPreviewImageUrl(userDetails.profile_image_url || '');
      setIsImageRemoved(false);
      setProfileImageFile(null);
      setErrors({});
    }
  };

  const handleUpdate = async () => {
    if (!validateFields()) return;

    try {
      setIsUpdating(true);

      const formPayload = new FormData();
      formPayload.append('user_id', userDetails.id);
      formPayload.append('first_name', formData.first_name);
      formPayload.append('last_name', formData.last_name);
      formPayload.append('email_id', formData.email_id);
      formPayload.append('contact_no', formData.contact_no);
      formPayload.append('role_id', formData.role_id);

      if (profileImageFile) {
        formPayload.append('profile_image', profileImageFile);
      }

      if (isImageRemoved && !profileImageFile) {
        formPayload.append('remove_image', 'true');
      }

      const res = await apiClient.put(`${siteUserPath}/update`, formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.status === "SUCCESS") {
        showSuccessToast(res.data.message || "User updated successfully");
        refreshUserList && refreshUserList();
        setIsEditMode(false);
        await fetchUserDetails(userDetails.id);
      } else {
        showErrorToast(res.data.message || "Update failed");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || "Error updating user";
      showErrorToast(msg);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="modal fade" id="editUserModal" tabIndex="-1" aria-labelledby="editUserModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="aprofile-content minh-auto">
            <div className="cpf-top">
              <h2 id="editUserModalLabel">
                {userDetails?.first_name} {userDetails?.last_name}
              </h2>
              {!isEditMode && canEditUser && (
                <button className="profile-edit" onClick={() => setIsEditMode(true)}>
                  <img src={editIcon} alt="Edit" />
                </button>
              )}
              <button className="m-close" data-bs-dismiss="modal" aria-label="Close">
                <img src={closeIcon} alt="Close" />
              </button>
            </div>

            <div className={`cprofile-inner ${!isEditMode ? 'view-mode' : 'edit-mode'}`}>
              <div className="row">
                <div className="col-md-5">
                  <div className="ap-left">
                    <div className="image-part">
                      <div className="upload-container" id="drop-area">
                        <div className="image-preview-wrapper">
                          {previewImageUrl ? (
                            <>
                              <img id="image-preview" src={previewImageUrl} alt="Profile" onError={(e) => { e.target.onerror = null; e.target.src = humanIcon; }} />
                              {isEditMode && (
                                <button className="remove-btn" onClick={() => {
                                  setProfileImageFile(null);
                                  setPreviewImageUrl('');
                                  setIsImageRemoved(true);
                                  const fileInput = document.getElementById('file-input');
                                  if (fileInput) fileInput.value = null;
                                }}>×</button>
                              )}
                            </>
                          ) : !isEditMode ? (
                            <img className='default-profileimage' src={humanIcon} alt="Default Avatar" />
                          ) : (
                            <div className="upload-text" onClick={() => document.getElementById('file-input').click()}>
                              <div className="upload-icon">
                                <img className="image-overlay" src={uploadIcon} alt="Upload" />
                              </div>
                              <h3>Click here, or <span>browse</span></h3>
                              <p>Dimension: 300px x 300px</p>
                            </div>
                          )}
                          {isEditMode && (
                            <input
                              type="file"
                              id="file-input"
                              accept={ALLOWED_IMAGE_TYPES}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setProfileImageFile(file);
                                  setPreviewImageUrl(URL.createObjectURL(file));
                                  setIsImageRemoved(false);
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-7">
                  <div className="ap-right">
                    <div className="aptab-header">
                      <ul className="nav nav-tabs tab-line" role="tablist">
                        <li className="nav-item">
                          <button className={`nav-link tab-btn ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
                            Info
                          </button>
                        </li>
                        <li className="nav-item">
                          <button className={`nav-link tab-btn ${!isEditMode ? 'disabled-tab' : ''} ${activeTab === 'password' ? 'active' : ''}`} disabled={!isEditMode} onClick={() => isEditMode && setActiveTab('password')}>
                            Password Management
                          </button>
                        </li>
                      </ul>
                    </div>

                    <div className="aptab-body">
                      <div className="tab-content">
                        <div className={`tab-pane fade ${activeTab === 'info' ? 'show active' : ''}`} id="info-tab-pane" role="tabpanel">
                          <InfoTab
                            formData={formData}
                            setFormData={setFormData}
                            isEditMode={isEditMode}
                            isLoading={isLoading}
                            roles={roles}
                            errors={errors}
                          />
                        </div>
                        <div className={`tab-pane fade ${activeTab === 'password' ? 'show active' : ''}`} id="password-tab-pane" role="tabpanel">
                          <PasswordTab
                            userId={userDetails?.id}
                            isEditMode={isEditMode}
                            setIsEditMode={setIsEditMode}
                            setActiveTab={setActiveTab} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {isEditMode && activeTab === 'info' && (
              <div className="cp-button-group">
                <button className="update-button" onClick={handleUpdate} disabled={isUpdating}>
                  {isUpdating ? <Loader /> : 'Update'}
                </button>

                <button className="cancel-button me-4 float-end" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;